import { Fragment, useEffect, useRef, useState } from "react";
import { Send, Mic, MicOff, Menu, Paperclip, X, Wand2, Phone } from "lucide-react";
import { toast } from "sonner";
import ChatMessage from "@/components/ChatMessage";
import CrisisCard from "@/components/CrisisCard";
import { IconTile, LunaIcon, hueRgb } from "@/components/icons/LunaIcon";
import { GlyphTile, GlowIcon } from "@/components/icons/GlyphTile";

// Any file type: the server decides what it can read.
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const MAX_FILES = 10;
// How close to the bottom still counts as "following the chat" (see the
// scroll effect below).
const NEAR_BOTTOM_PX = 80;

const QUICK_CHIPS = [
  { tr: "Bana motive edici bir söz söyle", en: "Give me a motivational quote" },
  { tr: "Bugün nasıl geçti anlatayım", en: "Let's talk about my day" },
  { tr: "Benimle sohbet et", en: "Let's just chat" },
];

const WORK_QUICK_CHIPS = [
  { tr: "Bu belgeyi özetle", en: "Summarize this document" },
  { tr: "Bu görselde ne var, anlat", en: "Tell me what's in this image" },
  { tr: "Bana yardım et", en: "Help me with something" },
];

// LunaWorks'ün sohbet giriş çubuğundaki Wand2 butonuna basınca açılan üretim
// menüsü. Hepsi (görsel dahil) aynı inline akışı kullanıyor: bir mod
// seçilir, kullanıcı yazar, gönderince Luna.jsx'teki ilgili generate*
// fonksiyonu çağrılır ve sonuç doğrudan sohbete düşer — ayrı bir modal
// pencere açılmıyor. `glyph` Luna'nın kendi (Celestial) ikonu — kenar
// çubuğundaki "Ürettiklerim" satırlarıyla aynı ikon ve renk.
const GEN_KIND_CONFIG = {
  image: {
    glyph: "images", tr: "Görsel", en: "Image",
    chip: { tr: "Görsel oluşturma modu — ne çizeyim?", en: "Image mode — what should I draw?" },
    placeholder: { tr: "Örn: mor tonlarda hilal ay şeklinde bir logo...", en: "E.g.: a crescent moon logo in purple tones..." },
  },
  pdf: {
    glyph: "pdf", tr: "PDF", en: "PDF",
    chip: { tr: "PDF modu — verini yaz ya da yapıştır", en: "PDF mode — write or paste your data" },
    placeholder: { tr: "Örn: geçen ayın gider listesi...", en: "E.g.: last month's expense list..." },
  },
  word: {
    glyph: "word", tr: "Word", en: "Word",
    chip: { tr: "Word modu — içeriğini yaz ya da yapıştır", en: "Word mode — write or paste your content" },
    placeholder: { tr: "Örn: proje raporu taslağı...", en: "E.g.: a project report draft..." },
  },
  ppt: {
    glyph: "ppt", tr: "PowerPoint", en: "PowerPoint",
    chip: { tr: "Sunum modu — konu başlıklarını yaz", en: "Slides mode — write your talking points" },
    placeholder: { tr: "Örn: yeni ürün lansmanı sunumu...", en: "E.g.: a new product launch deck..." },
  },
  excel: {
    glyph: "excel", tr: "Excel", en: "Excel",
    chip: { tr: "Excel modu — verini yaz ya da yapıştır", en: "Excel mode — write or paste your data" },
    placeholder: { tr: "Örn: haftalık satış rakamları...", en: "E.g.: weekly sales figures..." },
  },
  table: {
    glyph: "table", tr: "Tablo", en: "Table",
    chip: { tr: "Tablo modu — verini yaz ya da yapıştır", en: "Table mode — write or paste your data" },
    placeholder: { tr: "Örn: öğrenci not listesi...", en: "E.g.: a student grade list..." },
  },
  chart: {
    glyph: "chart", tr: "Grafik", en: "Chart",
    chip: { tr: "Grafik modu — sayısal verini yaz", en: "Chart mode — write your numeric data" },
    placeholder: { tr: "Örn: aylık gelir: Ocak 10bin, Şubat 12bin...", en: "E.g.: monthly revenue: Jan 10k, Feb 12k..." },
  },
};
const GEN_MENU_ORDER = ["image", "pdf", "word", "ppt", "excel", "table", "chart"];

// What the waiting bubble says Luna is making (pages/Luna.jsx `making`: the
// server's word from the stream's assistant_status, a wand request's kind,
// or lib/chatIntent.js's guess — "doc" when it only knows it is a file).
const MAKING_LINES = {
  image: { tr: "Görsel çiziliyor... 🎨", en: "Drawing your image... 🎨" },
  pptx: { tr: "Sunum hazırlanıyor... 📊", en: "Preparing your presentation... 📊" },
  xlsx: { tr: "Excel tablosu hazırlanıyor... 📈", en: "Preparing your Excel sheet... 📈" },
  docx: { tr: "Word belgesi hazırlanıyor... 📝", en: "Preparing your Word document... 📝" },
  pdf: { tr: "PDF hazırlanıyor... 📄", en: "Preparing your PDF... 📄" },
  tableimg: { tr: "Tablo görseli hazırlanıyor... 🧾", en: "Preparing your table image... 🧾" },
  chart: { tr: "Grafik hazırlanıyor... 📊", en: "Preparing your chart... 📊" },
  reading: { tr: "Dosya okunuyor… 📖", en: "Reading your file… 📖" },
  analyze: { tr: "Dosya inceleniyor… 🔍", en: "Inspecting your file… 🔍" },
  working: { tr: "Üzerinde çalışılıyor… ⚙️", en: "Working on it… ⚙️" },
  doc: { tr: "Oluşturuluyor... 📄", en: "Generating... 📄" },
};
// After this long the bubble adds that it takes a while (a deck or a
// workbook 1-4 minutes): a long build with nothing but a spinner reads as
// stuck, and a reload or a second request is what follows (audit client-04).
export const WAIT_HINT_S = 15;

// m:ss — 0:07, 1:42.
export function elapsedLabel(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// The status line's words while a request gets no answer at all (offline,
// a phone between networks): pages/Luna.jsx `waitingForNetwork`.
const WAITING_FOR_NETWORK = { tr: "Bağlantı bekleniyor…", en: "Waiting for connection…" };
// The waiting bubble's words for a LunaWorks generation (see WaitingBubble):
// a file 1-4 minutes, a picture well under one.
const LUNAWORKS_FILE_HINT = {
  tr: "Oluşturuluyor… 1–4 dk sürebilir, sayfayı kapatma", en: "Creating… this can take 1–4 min, keep this page open",
};
const LUNAWORKS_IMAGE_HINT = {
  tr: "Oluşturuluyor… birazdan hazır, sayfayı kapatma", en: "Creating… almost there, keep this page open",
};

// Luna's bubble while she works on a turn and no reply text has come yet:
// three dots — or, for a picture or a file, what she is making, how long it
// has taken so far and, after WAIT_HINT_S, how long it usually takes.
// stillWorking: the connection went and the server is still on it
// (lib/streamedTurn.js) — the page must stay open for it to land here.
// startedAt (ms): when the turn was sent — a turn picked up after a reload
// counts from then, not from the reload (hooks/usePendingTurn.js), and so
// does a LunaWorks generation picked up again (lib/generations.js).
// lunaWorks: the bubble is a LunaWorks generation's (the composer's wand) —
// from the start it says how long that takes and to keep the page open: a
// deck with nothing but a spinner for two minutes was reloaded and asked
// for again, and both were built (owner's test, 2026-10).
function WaitingBubble({ t, generatingImage, generatingDoc, making, etaS, stillWorking, startedAt, waitingForNetwork, lunaWorks = false, agentStep = null }) {
  // (otherwise the bubble's age is the turn's: it shows up when the turn starts)
  const shownAt = useRef(Date.now());
  const origin = Number.isFinite(startedAt) && startedAt <= shownAt.current ? startedAt : shownAt.current;
  const [seconds, setSeconds] = useState(() => Math.floor((Date.now() - origin) / 1000));
  const kind = generatingImage ? "image" : generatingDoc ? (MAKING_LINES[making] && making !== "image" ? making : "doc") : null;
  const counting = Boolean(kind || stillWorking);
  useEffect(() => {
    if (!counting) return undefined;
    const tick = () => setSeconds(Math.floor((Date.now() - origin) / 1000));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [counting, origin]);
  const long = (etaS || (kind === "image" ? 20 : 120)) >= 60;
  const hint = waitingForNetwork
    ? t(WAITING_FOR_NETWORK.tr, WAITING_FOR_NETWORK.en)
    : stillWorking
    ? t("Hâlâ hazırlanıyor, sayfayı kapatma 🌙", "Still working on it — don't close the page 🌙")
    : lunaWorks && kind
    ? (kind === "image"
      ? t(LUNAWORKS_IMAGE_HINT.tr, LUNAWORKS_IMAGE_HINT.en)
      : t(LUNAWORKS_FILE_HINT.tr, LUNAWORKS_FILE_HINT.en))
    : kind && seconds >= WAIT_HINT_S
    ? (long
      ? t("Bu 1–4 dk sürebilir, sayfayı açık tut.", "This can take 1–4 min — keep the page open.")
      : t("Birazdan hazır, sayfayı açık tut.", "Almost there — keep the page open."))
    : null;
  return (
    <div className="flex justify-start" data-testid="waiting-bubble">
      <div className="rounded-2xl border border-purple-400/20 bg-[#140b28]/90 px-4 py-3">
        {kind ? (
          <span className="flex items-baseline gap-2 text-xs text-purple-200/80">
            <span data-testid="waiting-making">{t(MAKING_LINES[kind].tr, MAKING_LINES[kind].en)}</span>
            <span className="tabular-nums text-purple-200/50" data-testid="waiting-elapsed">{elapsedLabel(seconds)}</span>
          </span>
        ) : (
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <span key={i} className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
        )}
        {agentStep && <p className="mt-1.5 text-[11px] text-purple-200/70" data-testid="waiting-agent-step">{agentStep}</p>}
        {hint && <p className="mt-1.5 text-[11px] text-purple-200/60" data-testid="waiting-hint">{hint}</p>}
      </div>
    </div>
  );
}

export default function FriendPanel({
  lang, messages, sending, listening, interim,
  input, setInput, onSend, onSendMedia, onSendMediaBatch, onToggleMic, onOpenCall,
  workMode, onGenerateImage, generatingImage, onGenerateDoc, generatingDoc, onOpenMobileMenu, onReport,
  making = null, etaS = null, stillWorking = false, agentStep = null,
  // startedAt: see WaitingBubble. waitingForNetwork: the turn's requests get
  // no answer at all. waitingHere: false while the turn being answered
  // belongs to another chat or mode (pages/Luna.jsx busyView) — its waiting
  // bubble isn't this chat's. work: a LunaWorks generation is running —
  // "here" (in this chat: its waiting bubble is the one shown) or
  // "elsewhere" (another chat or mode: the status line says so — a picture
  // or a file by workElsewhere: "image" | "doc"), else null. turnElsewhere:
  // a turn of another chat or mode is being answered (the status line says
  // so first — a generation of this chat may be showing its bubble here).
  startedAt = null, waitingForNetwork = false, waitingHere = true, work = null, workElsewhere = null,
  turnElsewhere = false,
}) {
  const t = (tr, en) => (lang === "tr" ? tr : en);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [genKind, setGenKind] = useState(null); // null | "image" | "pdf" | "word" | "ppt" | "excel" | "table" | "chart"
  const [genMenuOpen, setGenMenuOpen] = useState(false);
  const genMenuRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const hasMessages = messages.length > 0;
  // Only Luna's own greeting so far (Luna.jsx buildWelcomeMessage): still a
  // fresh chat, so the starter prompts show under it.
  const onlyWelcome = hasMessages && messages.every((m) => String(m.id).startsWith("welcome-"));
  const busy = sending || generatingImage || generatingDoc;
  // Luna's reply is streaming into its own bubble (lib/streamedTurn.js):
  // that bubble replaces the typing dots.
  const streaming = Boolean(messages[messages.length - 1]?.streaming);

  useEffect(() => {
    if (!genMenuOpen) return;
    const onClickOutside = (e) => {
      if (genMenuRef.current && !genMenuRef.current.contains(e.target)) setGenMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [genMenuOpen]);

  const handleGenMenuPick = (key) => {
    setGenMenuOpen(false);
    setPendingFiles([]);
    setGenKind(key);
  };

  // Accepts up to MAX_FILES at once (file picker's `multiple` attr or a
  // multi-file drag-and-drop) — invalid-type files are dropped individually
  // (one toast, not one per bad file) so a batch with one bad apple still
  // sends the rest.
  const acceptFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const valid = incoming.filter((f) => f.size <= MAX_FILE_BYTES);
    if (valid.length < incoming.length) {
      toast.error(t("Bazı dosyalar 15 MB'dan büyük, eklenmedi.", "Some files are over 15 MB and were not added."));
    }
    if (!valid.length) return;
    setGenKind(null);
    setPendingFiles((prev) => {
      const merged = [...prev, ...valid];
      if (merged.length > MAX_FILES) {
        toast.error(t(`En fazla ${MAX_FILES} dosya ekleyebilirsin.`, `You can attach at most ${MAX_FILES} files.`));
      }
      return merged.slice(0, MAX_FILES);
    });
  };

  const handleFilePick = (e) => {
    acceptFiles(e.target.files);
    e.target.value = ""; // lets picking the SAME file(s) again re-fire onChange
  };

  const removePendingFile = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Drag-and-drop straight from the OS (e.g. dragging a file out of the
  // browser's own download-history panel, as opposed to only the paperclip
  // button's file picker). dragDepth counts enter/leave pairs because
  // dragenter/dragleave fire once per CHILD element too — without it the
  // highlight flickers off the instant the pointer crosses into a child.
  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!e.dataTransfer?.types?.includes("Files")) return;
    dragDepth.current += 1;
    setDragOver(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault(); // required for onDrop to ever fire
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    acceptFiles(e.dataTransfer?.files);
  };

  // Ctrl+V a screenshot/copied image straight into the text field — browsers
  // expose a clipboard image as a File-like item (kind "file"), same shape
  // acceptFiles already handles for drag-and-drop and the file picker. Only
  // intercepts when the clipboard actually carries an image; a normal text
  // paste falls through untouched.
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = Array.from(items)
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (imageFiles.length) {
      e.preventDefault();
      acceptFiles(imageFiles);
    }
  };

  // The send button, and Enter in the text field. Nothing goes while Luna is
  // still busy — the button is disabled then, and Enter must not get around
  // it: the attached files and their note used to vanish, or a second (billed)
  // generation started next to the first (audit client-03). The composer is
  // cleared only once the send is taken (the handlers answer true / false —
  // pages/Luna.jsx; handleSend clears the text itself).
  const handleSendWithAttachment = () => {
    if (busy) return;
    if (genKind) {
      const prompt = input.trim();
      if (!prompt) return;
      const kind = genKind;
      const taken = kind === "image" ? onGenerateImage(prompt) : onGenerateDoc(kind, prompt);
      if (taken === false) return;
      setInput("");
      setGenKind(null);
      return;
    }
    if (pendingFiles.length) {
      const taken = pendingFiles.length === 1 ? onSendMedia(pendingFiles[0], input) : onSendMediaBatch(pendingFiles, input);
      if (taken === false) return;
      setPendingFiles([]);
      setInput("");
      return;
    }
    onSend();
  };

  // Enter sends — except while an input method is still composing a word
  // (Japanese, Chinese, Korean...: that Enter picks the word).
  const handleKeyDown = (e) => {
    if (e.key !== "Enter" || e.nativeEvent?.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    handleSendWithAttachment();
  };

  // The list follows new messages (and a streaming reply as it grows) only
  // while you are at — or within NEAR_BOTTOM_PX of — its bottom: scrolled up
  // to read something older, you stay put instead of being pulled down by
  // every streamed bit. Your own new message, or a whole new transcript
  // (another Sohbet, a mode switch), always goes to the bottom. pinnedRef is
  // measured on scroll, i.e. before the list grew.
  const pinnedRef = useRef(true);
  const lastSeenRef = useRef({ first: null, last: null });
  const handleListScroll = () => {
    const el = listRef.current;
    if (el) pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  };
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const first = messages[0]?.id ?? null;
    const last = messages[messages.length - 1];
    const seen = lastSeenRef.current;
    const newTranscript = first !== seen.first;
    const ownNewMessage = last?.role === "user" && last.id !== seen.last;
    lastSeenRef.current = { first, last: last?.id ?? null };
    if (pinnedRef.current || newTranscript || ownNewMessage) {
      el.scrollTop = el.scrollHeight;
      pinnedRef.current = true;
    }
  }, [messages, sending]);

  return (
    <div className="flex flex-col min-h-0 min-w-0 flex-1">
        <div className="flex items-center mb-1 gap-2">
          <button onClick={onOpenMobileMenu} data-testid="mobile-menu-button"
            aria-label={t("Menü", "Menu")}
            className="lg:hidden w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-white/60 border border-white/10 hover:bg-white/5">
            <Menu size={17} />
          </button>
          <div className="min-w-0">
            <p className="text-white/60 text-sm">{t("Merhaba! 👋", "Hey there! 👋")}</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white">
              {workMode
                ? <>{t("", "")}<span className="bg-gradient-to-r from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">LunaWorks</span>{t(" — görsel, PDF, belge, ne istersen.", " — images, PDFs, documents, whatever you need.")}</>
                : <>{t("Ben ", "I'm ")}<span className="bg-gradient-to-r from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">Luna</span>{t(", senin AI arkadaşın.", ", your AI companion.")}</>}
            </h1>
          </div>
          {!workMode && (
            <button onClick={onOpenCall} data-testid="open-voice-call-button"
              aria-label={t("Sesli ara", "Voice call")}
              className="ml-auto shrink-0 w-10 h-10 rounded-full flex items-center justify-center border border-purple-400/30 text-purple-200 hover:bg-purple-400/15 transition-colors"
              style={{ backgroundColor: "rgba(192,132,252,0.10)" }}>
              <Phone size={17} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2 mb-2">
          <span aria-hidden="true" className={`w-2 h-2 rounded-full ${sending || listening ? "bg-purple-400 animate-pulse" : "bg-emerald-400"}`} />
          {/* what the mic hears so far: shown, but outside the live region —
              a screen reader reading your own words back while the mic
              listens would be heard by the mic again */}
          {listening && interim ? (
            <span className="text-xs text-white/50" data-testid="chat-interim">{interim}</span>
          ) : null}
          {/* a live region: a screen reader hears when Luna thinks, writes,
              waits for the connection, is done (audit client-09) */}
          <span className={listening && interim ? "sr-only" : "text-xs text-white/50"} role="status" aria-live="polite"
            data-testid="chat-status-line">
            {listening ? t("Dinliyorum...", "Listening...")
              : sending && waitingForNetwork ? t(WAITING_FOR_NETWORK.tr, WAITING_FOR_NETWORK.en)
              : turnElsewhere || (sending && !waitingHere) ? t("Başka bir sohbetteki cevabın hazırlanıyor...", "Working on your reply in another chat...")
              : stillWorking ? t("Hâlâ hazırlanıyor...", "Still working on it...")
              : streaming ? t("Yazıyor...", "Writing...")
              : sending ? t("Düşünüyorum...", "Thinking...")
              : work === "here" ? t("Oluşturuluyor…", "Creating…")
              : work === "elsewhere" && workElsewhere === "image" ? t("Başka bir sohbette görselin çiziliyor...", "Your image is being drawn in another chat...")
              : work === "elsewhere" ? t("Başka bir sohbette dosyan hazırlanıyor...", "Your file is being made in another chat...")
              : t("Luna burada", "Luna is here")}
          </span>
        </div>

        <div className="relative flex-1 min-h-0 rounded-3xl border overflow-hidden flex flex-col transition-colors"
          onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
          style={{
            backgroundColor: dragOver ? "rgba(192,132,252,0.08)" : "rgba(255,255,255,0.035)",
            borderColor: dragOver ? "rgba(192,132,252,0.6)" : "rgba(192,132,252,0.15)",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
          }}>
          {!hasMessages && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
              <div style={{ width: 320, height: 320, borderRadius: "9999px",
                background: "radial-gradient(circle, rgba(192,132,252,0.16), transparent 70%)", filter: "blur(2px)" }} />
            </div>
          )}
          {dragOver && (
            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
              style={{ backgroundColor: "rgba(10,8,20,0.55)" }}>
              <div className="flex flex-col items-center gap-2 px-6 py-5 rounded-2xl border-2 border-dashed border-purple-400/60"
                style={{ backgroundColor: "rgba(20,11,40,0.9)" }}>
                <GlyphTile icon={Paperclip} hue="violet" size={48} />
                <p className="text-sm font-semibold text-white">{t("Bırak, Luna baksın 👀", "Drop it — Luna will take a look 👀")}</p>
              </div>
            </div>
          )}

          <div ref={listRef} data-testid="chat-message-list" onScroll={handleListScroll}
            className="relative flex-1 overflow-y-auto px-5 py-5 space-y-3 min-h-[220px]">
            {!hasMessages && (
              <div className="min-h-full flex flex-col items-center justify-center text-center gap-4">
                <IconTile name={workMode ? "work" : "friend"} size={56} />
                <p className="text-white/80 max-w-sm">
                  {workMode
                    ? t("Bir görsel/PDF yükle, bana bir görsel çizdir ya da ne yapmam gerektiğini yaz 📎🎨", "Upload an image/PDF, ask me to draw one, or just tell me what you need 📎🎨")
                    : t("Selam! Ben Luna 🌙 Bugün nasılsın?", "Hey! I'm Luna 🌙 How are you today?")}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {(workMode ? WORK_QUICK_CHIPS : QUICK_CHIPS).map((q, i) => (
                    <button key={i} data-testid="quick-prompt-chip" onClick={() => onSend(t(q.tr, q.en))}
                      className="text-xs px-3.5 py-1.5 rounded-full border border-purple-400/30 text-purple-200 hover:bg-purple-400 hover:text-black transition-all hover:-translate-y-0.5">
                      {t(q.tr, q.en)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <Fragment key={m.id}>
                <ChatMessage msg={m} lang={lang} onReport={onReport} />
                {/* (resources: the user's own country's help lines, sent with the reply) */}
                {m.safety && <CrisisCard kind={m.safety.kind} resources={m.safety.resources} lang={lang} />}
              </Fragment>
            ))}
            {onlyWelcome && !busy && (
              <div className="flex flex-wrap gap-2 pl-9" data-testid="starter-prompts">
                {(workMode ? WORK_QUICK_CHIPS : QUICK_CHIPS).map((q, i) => (
                  <button key={i} data-testid="quick-prompt-chip" onClick={() => onSend(t(q.tr, q.en))}
                    className="text-xs px-3.5 py-1.5 rounded-full border border-purple-400/30 text-purple-200 hover:bg-purple-400 hover:text-black transition-all hover:-translate-y-0.5">
                    {t(q.tr, q.en)}
                  </button>
                ))}
              </div>
            )}
            {busy && !streaming && waitingHere && (
              <WaitingBubble t={t} generatingImage={generatingImage} generatingDoc={generatingDoc}
                making={making} etaS={etaS} stillWorking={stillWorking} startedAt={startedAt}
                waitingForNetwork={sending && waitingForNetwork} lunaWorks={work === "here"} agentStep={agentStep} />
            )}
          </div>

          <div className="relative p-3 border-t border-white/5">
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-2 mx-1">
                {pendingFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} data-testid="pending-attachment-chip"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-400/10 border border-purple-400/25 text-xs text-purple-100 max-w-[180px]">
                    <GlowIcon icon={Paperclip} hue="violet" size={13} />
                    <span className="truncate">{f.name}</span>
                    <button onClick={() => removePendingFile(i)} data-testid="remove-attachment-button"
                      aria-label={t("Dosyayı kaldır", "Remove file")}
                      className="shrink-0 text-purple-300/70 hover:text-white transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {pendingFiles.length > 1 && (
                  <button onClick={() => setPendingFiles([])} data-testid="clear-all-attachments-button"
                    className="text-[11px] text-white/40 hover:text-white/70 px-1.5">
                    {t("Tümünü kaldır", "Clear all")}
                  </button>
                )}
              </div>
            )}
            {genKind && (
              <div data-testid="generate-mode-chip"
                className="flex items-center gap-2 mb-2 mx-1 px-3 py-1.5 rounded-full bg-fuchsia-400/10 border border-fuchsia-400/25 text-xs text-fuchsia-100 w-fit max-w-full">
                <LunaIcon name={GEN_KIND_CONFIG[genKind].glyph} size={16}
                  style={{ filter: `drop-shadow(0 0 4px rgba(${hueRgb(GEN_KIND_CONFIG[genKind].glyph)},.55))` }} />
                <span>{t(GEN_KIND_CONFIG[genKind].chip.tr, GEN_KIND_CONFIG[genKind].chip.en)}</span>
                <button onClick={() => setGenKind(null)} data-testid="cancel-generate-mode-button"
                  aria-label={t("İptal et", "Cancel")}
                  className="shrink-0 text-fuchsia-300/70 hover:text-white transition-colors">
                  <X size={12} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" multiple onChange={handleFilePick}
                className="hidden" data-testid="chat-file-input" />
              <button data-testid="attach-file-button" onClick={() => fileInputRef.current?.click()}
                aria-label={t("Görsel veya belge ekle", "Attach an image or document")}
                className="w-11 h-11 rounded-full flex items-center justify-center transition-all shrink-0 border border-purple-400/25 text-purple-200 hover:bg-purple-400/15">
                <Paperclip size={16} />
              </button>
              {workMode && (
                <div ref={genMenuRef} className="relative shrink-0">
                  <button data-testid="generate-image-toggle-button"
                    onClick={() => setGenMenuOpen((v) => !v)}
                    aria-label={t("Görsel/belge oluştur", "Generate an image/document")}
                    className={`w-11 h-11 rounded-full flex items-center justify-center transition-all shrink-0 border ${
                      genKind || genMenuOpen ? "bg-fuchsia-400/20 border-fuchsia-400 text-fuchsia-200" : "border-purple-400/25 text-purple-200 hover:bg-purple-400/15"}`}>
                    <Wand2 size={16} />
                  </button>
                  {genMenuOpen && (
                    <div data-testid="generate-menu" role="menu"
                      className="absolute bottom-full left-0 mb-2 w-44 rounded-2xl border border-purple-400/25 p-1.5 shadow-2xl shadow-black/40 z-20"
                      style={{ backgroundColor: "rgba(15,10,28,0.97)", backdropFilter: "blur(10px)" }}>
                      {GEN_MENU_ORDER.map((key) => {
                        const it = GEN_KIND_CONFIG[key];
                        return (
                          <button key={key} data-testid={`generate-menu-item-${key}`}
                            onClick={() => handleGenMenuPick(key)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-purple-100 hover:bg-purple-400/15 hover:text-white transition-colors text-left">
                            <LunaIcon name={it.glyph} size={18} />
                            {t(it.tr, it.en)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {/* 16 px on phones (text-base): iOS Safari zooms into any
                  field under 16 px when it is tapped (audit client-05) */}
              <input data-testid="chat-input-field" value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={genKind
                  ? t(GEN_KIND_CONFIG[genKind].placeholder.tr, GEN_KIND_CONFIG[genKind].placeholder.en)
                  : pendingFiles.length
                  ? t("İsteğe bağlı bir not ekle...", "Add an optional note...")
                  : t("Luna'ya bir şey yaz...", "Write something to Luna...")}
                className="flex-1 min-w-0 bg-black/25 outline-none px-3 sm:px-4 py-2.5 rounded-full border border-purple-400/20 focus:border-purple-400 text-base sm:text-sm text-white placeholder:text-white/30 transition-colors" />
              <button data-testid="mic-toggle-button" onClick={onToggleMic}
                aria-label={t("Sesli konuş", "Speak")}
                className={`w-11 h-11 rounded-full flex items-center justify-center transition-all shrink-0 ${
                  listening ? "bg-red-500 text-white shadow-[0_0_18px_rgba(239,68,68,0.5)]" : "border border-purple-400/25 text-purple-200 hover:bg-purple-400/15"}`}>
                {listening ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
              <button data-testid="send-message-button" onClick={handleSendWithAttachment}
                disabled={(!input.trim() && !pendingFiles.length) || busy} aria-label={t("Gönder", "Send")}
                className="w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-40 hover:scale-105 shrink-0 bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
                <Send size={17} aria-hidden="true" />
              </button>
            </div>
            <p data-testid="ai-disclaimer" className="mt-2 px-2 text-center text-xs text-white/60">
              {t("Luna bir yapay zekadır; hata yapabilir ve profesyonel desteğin yerini tutmaz.",
                "Luna is an AI. It can make mistakes and can't replace professional support.")}
            </p>
          </div>
        </div>
    </div>
  );
}
